import React, { useMemo, useState } from 'react';
import './AiVisibilityReportPage.scss';
import Title from '../../shared/components/title/Title';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../shared/routes/path';
import { useGetAiVisibilityRuns } from '../../shared/api/hooks/ai-visibility-report/useGetAiVisibilityRuns';
import { useGetAiVisibilityReports } from '../../shared/api/hooks/ai-visibility-report/useGetAiVisibilityReports';
import { useGetAiVisibilityUsage } from '../../shared/api/hooks/ai-visibility-report/useGetAiVisibilityUsage';
import { useGenerateAiVisibilityReport } from '../../shared/api/hooks/ai-visibility-report/useGenerateAiVisibilityReport';
import {
  useDeleteAiVisibilityRun,
  useDeleteAiVisibilityReport,
} from '../../shared/api/hooks/ai-visibility-report/useDeleteAiVisibilityReport';
import { downloadAiVisibilityReport } from '../../shared/api/services/ai-visibility-report/AiVisibilityReportService';
import { downloadBlobAsFile } from '../../shared/utils/utils';
import { AI_VISIBILITY_REPORTS_DEFAULT_PAGE_SIZE } from '../../shared/models/ai-visibility-report/AiVisibilityReportOverview';
import AiVisibilityReportForm from './components/AiVisibilityReportForm';
import AiVisibilityReportList from './components/AiVisibilityReportList';
import Loader from '../../shared/components/common/loader/Loader';
import axios from 'axios';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useGetDateRange } from '../../shared/api/hooks/monitor/useGetDateRange';
import { toLocalDay } from '../../shared/helpers/filters/DateRangeHelper';
import { startOfDay } from 'date-fns';

const parseProjectId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const AiVisibilityReportPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse } = useAccount();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = parseProjectId(projectIdParam);
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AI_VISIBILITY_REPORTS_DEFAULT_PAGE_SIZE);
  const [first, setFirst] = useState(0);

  const { data: runs, isLoading: isRunsLoading } = useGetAiVisibilityRuns(projectId);
  const { data: reportsPage, isLoading: isReportsLoading } = useGetAiVisibilityReports(
    projectId,
    page,
    pageSize
  );
  const { data: usage } = useGetAiVisibilityUsage(projectId != null);
  const { dateRange } = useGetDateRange(projectId);
  const pickerBounds = useMemo(() => {
    const minDate = dateRange?.oldestDate ? toLocalDay(dateRange.oldestDate) : undefined;
    return { minDate, maxDate: startOfDay(new Date()) };
  }, [dateRange?.oldestDate]);
  const generate = useGenerateAiVisibilityReport(projectId ?? 0, {
    onSuccess: () => {
      setPage(1);
      setFirst(0);
    },
  });
  const deleteRun = useDeleteAiVisibilityRun(projectId ?? 0);
  const deleteReport = useDeleteAiVisibilityReport(projectId ?? 0);

  if (accountResponse && !accountResponse.addons.includes('AI_VISIBILITY_REPORT')) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (!accountResponse) {
    return null;
  }

  if (projectId == null) {
    return <Navigate to={PROJECTS} replace />;
  }

  const handleGenerate = (from: string, to: string) => {
    setFormError(null);
    generate.mutate(
      { from, to },
      {
        onError: (err) => {
          if (axios.isAxiosError(err)) {
            const code = err.response?.data?.error;
            if (code === 'report_exists') {
              setFormError(t('aiVisibilityReport.errorReportExists'));
              return;
            }
            if (code === 'active_run_exists') {
              setFormError(t('aiVisibilityReport.errorActiveRun'));
              return;
            }
            if (code === 'monthly_limit_reached') {
              // Second line of defence: the button is disabled, but the quota can be spent in
              // another tab between the usage fetch and this submit.
              setFormError(t('aiVisibilityReport.errorMonthlyLimit'));
              return;
            }
            if (typeof err.response?.data?.error === 'string') {
              setFormError(err.response.data.error);
              return;
            }
          }
          setFormError(t('aiVisibilityReport.errorGeneric'));
        },
      }
    );
  };

  const handleDownload = async (reportId: number, periodFrom: string, periodTo: string) => {
    try {
      const blob = await downloadAiVisibilityReport(reportId);
      downloadBlobAsFile(
        blob,
        `ai-visibility-report-${projectId}-${periodFrom}_${periodTo}.pdf`
      );
    } catch {
      setFormError(t('aiVisibilityReport.errorDownload'));
    }
  };

  const handlePage = (event: { first: number; rows: number }) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    if (newPage !== page || event.rows !== pageSize) {
      setPage(newPage);
      setPageSize(event.rows);
      setFirst(event.first);
    }
  };

  const confirmDeleteReport = (reportId: number) => {
    confirmDialog({
      message: t('aiVisibilityReport.confirmDeleteReport'),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () => deleteReport.mutate(reportId),
    });
  };

  const confirmDismissFailed = (runId: number) => {
    confirmDialog({
      message: t('aiVisibilityReport.confirmDismissRun'),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () => deleteRun.mutate(runId),
    });
  };

  const confirmCancelRun = (runId: number) => {
    confirmDialog({
      message: t('aiVisibilityReport.confirmCancelRun'),
      header: t('aiVisibilityReport.confirmCancelRunHeader'),
      defaultFocus: 'accept',
      accept: () => deleteRun.mutate(runId),
    });
  };

  const isLoading = isRunsLoading || isReportsLoading;

  return (
    <div className="ai-visibility-report-page page-content">
      <div className="page-box">
        {/* Telefon: tytuł w górnym pasku zamiast nagłówka strony. */}
        <MobilePageHeader title={<span>{t('aiVisibilityReport.title')}</span>} />
        {!isMobile && (
          <Title
            title={t('aiVisibilityReport.title')}
            childrenOnMain={
              <div className="title-box__subtitle">{t('aiVisibilityReport.subtitle')}</div>
            }
          />
        )}

        <div className="page-box__stats">
          <AiVisibilityReportForm
            isBusy={generate.isPending}
            onGenerate={handleGenerate}
            error={formError}
            minDate={pickerBounds.minDate}
            maxDate={pickerBounds.maxDate}
            usage={usage}
          />

          {isLoading ? (
            <Loader />
          ) : (
            <AiVisibilityReportList
              activeRuns={runs?.activeRuns ?? []}
              failedRuns={runs?.failedRuns ?? []}
              reports={reportsPage?.items ?? []}
              totalReports={reportsPage?.total ?? 0}
              first={first}
              pageSize={pageSize}
              onPage={handlePage}
              onDismissFailed={confirmDismissFailed}
              onCancelRun={confirmCancelRun}
              onDeleteReport={confirmDeleteReport}
              onDownload={handleDownload}
              busy={generate.isPending || deleteRun.isPending || deleteReport.isPending}
            />
          )}
        </div>
      </div>

      <ConfirmDialog className="dialog-box" />
    </div>
  );
};

export default AiVisibilityReportPage;
